import type { RJSFSchema } from "@rjsf/utils";
import { get, useController } from "react-hook-form";
import { MAX_POSTGRES_COLUMN_NAME_LENGTH } from "common/constants";
import type { PathArray } from "common/utils/formStructure";
import { BANNER_TYPES } from "../../../../../../../Common/__styles__/Banner";
import Banner from "../../../../../../../Common/Banner";
import { Body, Title } from "../../../../../../../Common/Typography";
import { Text } from "../../../../../../../Inputs/react-hook-form";
import {
  getUiSchemaPropertyFormPath,
  useBuilderFormContext,
  useBuilderWatch,
} from "../../../form/form";
import {
  FieldWrapper,
  FormSection,
  FormSectionTitleWrapper,
} from "../../__styles__/SidebarColumn";
import { ColumnHeaderWrapper } from "./__styles__/DisplaySettings";

type DisplaySettingsProps = {
  fieldPath: PathArray;
  isNestedField?: boolean;
  isReadOnly?: boolean;
};

export function DisplaySettings({
  fieldPath,
  isNestedField,
  isReadOnly,
}: DisplaySettingsProps) {
  const { register } = useBuilderFormContext();

  const [formTitle, schema] = useBuilderWatch(["name", "formStructure.schema"]);

  const fieldTitles = getFieldTitles(schema, fieldPath, formTitle);
  const joinedTitles = fieldTitles.join(" > ");

  const columnHeaderOverridePropertyFormPath = getUiSchemaPropertyFormPath(
    fieldPath,
    "columnHeaderOverride",
  );

  const { field, fieldState } = useController({
    name: columnHeaderOverridePropertyFormPath,
  });

  const columnHeaderOverride = field.value;

  const defaultHeaderTooLong =
    joinedTitles.length >
    (isNestedField ? 30 : MAX_POSTGRES_COLUMN_NAME_LENGTH);
  const needsOverride = defaultHeaderTooLong && !columnHeaderOverride;

  const columnHeaderOverrideError = fieldState.error;

  return (
    <FormSection>
      <FormSectionTitleWrapper>
        <Title size="small" type="semiBold">
          Display settings
        </Title>
      </FormSectionTitleWrapper>
      <Body size="default" type="regular">
        This field will appear in tables and exported files with the header:
      </Body>
      <ColumnHeaderWrapper>
        <Body size="default" type="regular">
          {columnHeaderOverride || joinedTitles}
        </Body>
      </ColumnHeaderWrapper>
      {needsOverride && (
        <Banner type={BANNER_TYPES.ATTENTION} title="">
          <Body size="default" type="emphasis">
            The default column header is too long for exports.
          </Body>
          <Body size="default" type="regular">
            Provide an override label to ensure this field displays properly.
          </Body>
        </Banner>
      )}
      <FieldWrapper>
        <Text
          label="Column header override label"
          description="Use a shorter or alternate label for this field in tables and file exports."
          required={defaultHeaderTooLong}
          error={columnHeaderOverrideError?.message}
          maxLength={30}
          helperText="Max 30 characters"
          disabled={isReadOnly}
          {...register(columnHeaderOverridePropertyFormPath, {
            required:
              defaultHeaderTooLong ?
                "Column header override label is required"
              : undefined,
            maxLength: 30,
          })}
        />
      </FieldWrapper>
    </FormSection>
  );
}

function getFieldTitles(
  schema: RJSFSchema,
  fieldPath: PathArray,
  formTitle: string | undefined,
) {
  const titles: Array<string | undefined> = [formTitle];

  // for: 123.456.789
  // generate:
  //  properties.123.title
  //  properties.123.properties.456.title
  //  properties.123.properties.456.properties.789.title

  const fieldRootPaths = fieldPath.map((_part, index, arr) => {
    return `properties.${arr.slice(0, index + 1).join(".properties.")}.title`;
  });

  for (const fieldRootPath of fieldRootPaths) {
    titles.push(get(schema, fieldRootPath));
  }

  return titles.filter(Boolean);
}
